import type { KeyboardEvent } from "react";
import { cx } from "./klasy";
import type { KomponentIkony } from "./ikony";
import "../styles/wspolne.css";

export interface OpcjaFiltra<W extends string = string> {
  wartosc: W;
  etykieta: string;
  /** Liczba wyników przy opcji, np. „Online 3” */
  ile?: number;
  ikona?: KomponentIkony;
  wylaczona?: boolean;
}

export interface ZetonyProps<W extends string> {
  etykieta: string;
  opcje: ReadonlyArray<OpcjaFiltra<W>>;
  /** Wybrane wartości (jedna przy pojedynczym wyborze) */
  wybrane: ReadonlyArray<W>;
  onPrzelacz: (wartosc: W) => void;
  /** Na telefonie żetony przewijają się w poziomie; zawijane = łamią się do kolejnych wierszy */
  zawijane?: boolean;
  className?: string;
}

/** Żetony filtrów (przyciski z aria-pressed). O tym, czy wybór jest pojedynczy, decyduje aplikacja w onPrzelacz. */
export function Zetony<W extends string>({ etykieta, opcje, wybrane, onPrzelacz, zawijane, className }: ZetonyProps<W>) {
  return (
    <div className={cx("iot-zetony", zawijane && "iot-zetony--zawijane", className)} role="group" aria-label={etykieta}>
      {opcje.map(({ wartosc, etykieta: e, ile, ikona: Ikona, wylaczona }) => (
        <button key={wartosc} type="button" className="iot-zeton" aria-pressed={wybrane.includes(wartosc)} disabled={wylaczona} onClick={() => onPrzelacz(wartosc)}>
          {Ikona && <Ikona />}{e}{ile !== undefined && <span className="iot-zeton__ile">{ile}</span>}
        </button>
      ))}
    </div>
  );
}

export interface SegmentyProps<W extends string> {
  etykieta: string;
  opcje: ReadonlyArray<OpcjaFiltra<W>>;
  wartosc: W;
  onZmien: (wartosc: W) => void;
  /** Rozciąga segmenty na całą szerokość */
  pelne?: boolean;
  className?: string;
}

/** Przełącznik segmentowy (role="radiogroup", strzałki zmieniają wybór). */
export function Segmenty<W extends string>({ etykieta, opcje, wartosc, onZmien, pelne, className }: SegmentyProps<W>) {
  const aktywne = opcje.filter((o) => !o.wylaczona);
  function klawisz(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const i = aktywne.findIndex((o) => o.wartosc === wartosc);
    const nastepny = aktywne[(i + (e.key === "ArrowRight" ? 1 : aktywne.length - 1)) % aktywne.length];
    if (nastepny) onZmien(nastepny.wartosc);
  }
  return (
    <div className={cx("iot-segmenty", pelne && "iot-segmenty--pelne", className)} role="radiogroup" aria-label={etykieta} onKeyDown={klawisz}>
      {opcje.map(({ wartosc: w, etykieta: e, ikona: Ikona, wylaczona }) => (
        <button key={w} type="button" role="radio" className="iot-segmenty__opcja" aria-checked={w === wartosc} tabIndex={w === wartosc ? 0 : -1} disabled={wylaczona} onClick={() => onZmien(w)}>
          {Ikona && <Ikona />}{e}
        </button>
      ))}
    </div>
  );
}
